import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import {
	deliveryMethodContainerStyles,
	errorMessageStyles,
	formGroupStyles,
	formLabelStyles,
	getDeliveryMethodOptionStyles,
	getPhoneInputWrapperStyles,
	getSubmitButtonStyles,
	iconContainerStyles,
	loadingSpinnerStyles,
	modalBodyStyles,
	modalHeaderStyles,
	modalSubtitleStyles,
	modalTitleStyles,
	phoneInputStyles,
	phonePrefixStyles,
	stepTransitionRightStyles,
} from './styled';
import type { PhoneInputStepProps } from './types';

const PhoneInputStep: FC<PhoneInputStepProps> = ({
	phoneNumber,
	setPhoneNumber,
	deliveryMethod,
	setDeliveryMethod,
	onSendCode,
	isLoading,
	error,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const handlePhoneChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value.replace(/\D/g, '').slice(0, 11);
			setPhoneNumber(value);
		},
		[setPhoneNumber]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter' && phoneNumber.length >= 10) {
				e.preventDefault();
				onSendCode();
			}
		},
		[phoneNumber, onSendCode]
	);

	const isValidPhone = phoneNumber.length >= 10;
	const isDisabled = !isValidPhone || isLoading;

	return (
		<div style={stepTransitionRightStyles}>
			<div style={modalHeaderStyles}>
				<div
					style={iconContainerStyles}
					aria-hidden='true'
				>
					<i className='fas fa-mobile-alt' />
				</div>
				<h2 style={modalTitleStyles}>Verify Your Phone Number</h2>
				<p style={modalSubtitleStyles}>We&apos;ll send you a verification code to confirm your identity</p>
			</div>

			<div style={modalBodyStyles}>
				{error && (
					<div
						style={errorMessageStyles}
						role='alert'
					>
						<i
							className='fas fa-exclamation-triangle'
							aria-hidden='true'
						/>
						{error}
					</div>
				)}

				<div style={formGroupStyles}>
					<label
						style={formLabelStyles}
						htmlFor='phone-input'
					>
						Phone Number
					</label>
					<div style={getPhoneInputWrapperStyles(!!error)}>
						<div style={phonePrefixStyles}>+234</div>
						<input
							ref={inputRef}
							id='phone-input'
							type='tel'
							inputMode='numeric'
							placeholder='Enter your phone number'
							value={phoneNumber}
							onChange={handlePhoneChange}
							onKeyDown={handleKeyDown}
							disabled={isLoading}
							autoComplete='tel-national'
							aria-describedby={error ? 'phone-error' : undefined}
							style={phoneInputStyles}
						/>
					</div>
				</div>

				<div style={formGroupStyles}>
					<label style={formLabelStyles}>Send Code Via</label>
					<div
						style={deliveryMethodContainerStyles}
						role='radiogroup'
					>
						<button
							type='button'
							role='radio'
							aria-checked={deliveryMethod === 'sms'}
							onClick={() => setDeliveryMethod('sms')}
							disabled={isLoading}
							style={getDeliveryMethodOptionStyles(deliveryMethod === 'sms')}
						>
							<i
								className='fas fa-sms'
								aria-hidden='true'
							/>
							SMS
						</button>
						<button
							type='button'
							role='radio'
							aria-checked={deliveryMethod === 'whatsapp'}
							onClick={() => setDeliveryMethod('whatsapp')}
							disabled={isLoading}
							style={getDeliveryMethodOptionStyles(deliveryMethod === 'whatsapp')}
						>
							<i
								className='fab fa-whatsapp'
								aria-hidden='true'
							/>
							WhatsApp
						</button>
					</div>
				</div>

				<button
					type='button'
					onClick={onSendCode}
					disabled={isDisabled}
					style={getSubmitButtonStyles(isDisabled)}
				>
					{isLoading ? (
						<>
							<span style={loadingSpinnerStyles} />
							Sending...
						</>
					) : (
						<>
							<i
								className='fas fa-paper-plane'
								aria-hidden='true'
							/>
							Send Verification Code
						</>
					)}
				</button>
			</div>
		</div>
	);
};

export default PhoneInputStep;
